import { useCallback, useMemo, useState } from 'react';
import Link from 'next/link';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import BaseCheckbox from '@r1-frontend/ui-react/components/controls/baseCheckbox';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import DefaultPopup from '@r1-frontend/ui-react/components/popups/defaultPopup';
import { CallCalling } from '@r1-frontend/ui-react/components/svg/call';
import { H2 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph3 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import { RoundedBlock } from '@r1-frontend/ui-react/experimental/blocks';
import { Cost } from '@r1-frontend/ui-react/experimental/purchase/cost';
import { TSpeedLineItemWithDescription } from '@r1-frontend/ui-react/organisms/SpeedLine/SpeedLineWithDescription';
import { ACTIONS, CATEGORIES } from '@r1-frontend/shared/constants/analytics';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { useOnceExecute } from '@r1-frontend/shared/hooks/useOnceExecute';
import { TArrayTwoOrMore } from '@r1-frontend/shared/types/Array';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import LazyCallBack from '~/src/components/LazyCallBack';
import getUriToFullBuy from '~/src/features/request/helpers/getUriToFullBuy';
import { TTariffsGroup } from '~/src/features/request/types/TTariffsGroup';
import { getAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';
import { isLeadGenerationHide } from '~/src/helpers/isLeadGenerationHide';

import * as ST from '~/src/features/request/components/TariffSpeedSelector/styled';

type TSpeedLineSelectorProps = {
    speedLineItems: {
        bundles: TArrayTwoOrMore<TSpeedLineItemWithDescription>,
        internet: TArrayTwoOrMore<TSpeedLineItemWithDescription>,
    },
    isAuth: boolean,
}
const sendAnalytics = getAnalyticsSender(CATEGORIES.applicationForm);
const sendAnalyticsCard = getAnalyticsSender(CATEGORIES.tariffCards);
const SpeedLineSelector = (
    {
        speedLineItems,
        isAuth,
    }: TSpeedLineSelectorProps,
): JSX.Element => {
    const isTablet = useMatchMedia(`(max-width: ${wideBreakpoints.tablet})`);
    const [selectedSpeedItemId, setSelectedSpeedItemId] = useState(0);
    const [isLazyCallbackOpen, setIsLazyCallbackOpen] = useState(false);
    const [tariffsGroup, setTariffsGroup] = useState<TTariffsGroup>('bundles');

    const sendAnalyticsOnce = useOnceExecute(()=> sendAnalyticsCard(ACTIONS.moveSpeedSlider));
    selectedSpeedItemId && sendAnalyticsOnce();

    const isLeadGeneration = useMemo(() => isLeadGenerationHide(), []);
    const selectedSpeedItem = speedLineItems[tariffsGroup][selectedSpeedItemId];
    const minPrice = selectedSpeedItem.price;
    const redirectLink = isAuth ? '/lk/change-tariff' : getUriToFullBuy(selectedSpeedItem.alias, tariffsGroup);
    const handleSetIsTvChannelsNeed = () => {
        setTariffsGroup(prev => prev === 'bundles' ? 'internet' : 'bundles');
        tariffsGroup === 'bundles'
            ? sendAnalyticsCard(ACTIONS.checkboxTvChannelsOff)
            : sendAnalyticsCard(ACTIONS.checkboxTvChannelsOn);

        setSelectedSpeedItemId(0);
    };

    const onClickHandler = () => {
        !isAuth && sendAnalytics(
            'open_tariff',
            `${selectedSpeedItem.title}|${tariffsGroup === 'bundles' ? 'bundle' : 'mono'}`,
        );
    };

    const showLazyCallback = useCallback(() => {
        sendAnalytics(ACTIONS.openCallbackForm);
        setIsLazyCallbackOpen(true);
    }, []);

    return (
        <>
            <TextWrapper>
                <H2 as='h1' $smaller>Подключите Дом.ру онлайн</H2>
                <Paragraph3 $withIndent>Выберите тариф и настройте его под себя за пару минут</Paragraph3>
            </TextWrapper>

            <ST.SpeedLineSelectorWrapper>
                <ST.SSpeedLineWithDescription
                    items={speedLineItems[tariffsGroup]}
                    startDot={selectedSpeedItemId}
                    onSelect={setSelectedSpeedItemId}
                />
                <RoundedBlock>
                    <BaseCheckbox
                        checked={tariffsGroup === 'bundles'}
                        onChange={handleSetIsTvChannelsNeed}
                    >
                        А еще мне нужны ТВ-каналы
                    </BaseCheckbox>
                </RoundedBlock>
                <ST.BtnWrap>
                    <Link href={redirectLink} passHref>
                        <Button bType={'action'} size={'medium'} onClick={onClickHandler}>
                            <span>Подключить {minPrice ? <>от&nbsp;<Cost>{minPrice}</Cost></> : ''}</span>
                        </Button>
                    </Link>
                    {!isLeadGeneration &&
                        <Button
                            bType={'secondary'}
                            size={'medium'}
                            rounded
                            Icon={<CallCalling/>}
                            onClick={showLazyCallback}
                        />
                    }
                </ST.BtnWrap>
            </ST.SpeedLineSelectorWrapper>
            <DefaultPopup
                isOpen={isLazyCallbackOpen}
                onClose={() => setIsLazyCallbackOpen(false)}
                width={isTablet ? '360px' : '600px'}
            >
                <LazyCallBack
                    tariffName={selectedSpeedItem?.title}
                    requestDataParams={{
                        product_id: tariffsGroup === 'bundles'
                            ? [EProductsId.internet, EProductsId.tv]
                            : [EProductsId.internet],
                    }}
                    withPopupResult={true}
                />
            </DefaultPopup>
        </>
    );
};

export default SpeedLineSelector;
